import { useCallback, useRef } from 'react';
import { useQueryClient, type QueryKey } from '@tanstack/react-query';

export type PrefetchQueryContext = {
  signal?: AbortSignal;
};

export type PrefetchConfig<TData = unknown> = {
  queryKey: QueryKey;
  queryFn: (context: PrefetchQueryContext) => Promise<TData>;
  staleTime?: number;
  isInfinite?: boolean;
  onSuccess?: (data: TData) => void;
};

type UsePrefetchOptions = {
  delay?: number;
};

type UsePrefetchReturn = {
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onFocus: () => void;
  prefetch: () => void;
};

export function usePrefetch<TData = unknown>(
  config: PrefetchConfig<TData> | undefined,
  options: UsePrefetchOptions = {}
): UsePrefetchReturn {
  const { delay = 100 } = options;
  const queryClient = useQueryClient();
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearPrefetchTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const prefetch = useCallback(() => {
    if (!config) return;

    const { queryKey, queryFn, staleTime, isInfinite, onSuccess } = config;

    if (isInfinite) {
      queryClient
        .fetchInfiniteQuery({
          queryKey,
          queryFn,
          staleTime,
        })
        .then((data) => {
          onSuccess?.(data as TData);
        })
        .catch(() => { });

      return;
    }

    queryClient
      .fetchQuery({
        queryKey,
        queryFn,
        staleTime,
      })
      .then((data) => {
        onSuccess?.(data);
      })
      .catch(() => { });
  }, [config, queryClient]);

  const schedulePrefetch = useCallback(() => {
    if (!config) return;

    clearPrefetchTimeout();

    if (delay > 0) {
      timeoutRef.current = setTimeout(prefetch, delay);
    } else {
      prefetch();
    }
  }, [config, delay, clearPrefetchTimeout, prefetch]);

  const onMouseEnter = useCallback(() => schedulePrefetch(), [schedulePrefetch]);
  const onMouseLeave = useCallback(() => clearPrefetchTimeout(), [clearPrefetchTimeout]);
  const onFocus = useCallback(() => prefetch(), [prefetch]);

  return { onMouseEnter, onMouseLeave, onFocus, prefetch };
}
